import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";
import FormErrors from "@/components/FormErrors/FormErrors.vue";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";

import { v4 as uuidv4 } from "uuid";
import { JSConn } from "@/utilities/js-conn";
import { map as Rmap } from "ramda";

import { NamedRts } from "@/router";
import { buildSubmitButton, buildFormErrors } from "@/utilities/form-helpers";

import { TOAST_OPTIONS } from "@/constants";

import {
  buildFormClient,
  prettifyClientStatus,
  prettifyPhoneNumberClassification,
} from "@/models/Client";

import { Client } from "@/store/modules/clients";

export default defineComponent({
  name: "clientEditForm",
  components: {
    FormErrors,
    LoadingAnimation,
  },
  props: {
    clientId: {
      type: String,
      required: true,
    },
  },

  setup(props, ctx) {
    let $store = ctx.root.$store;

    let dataLoaded = ref(false);
    let loadingMessage = ref("Preparing the form...");

    let firstInput: any = ref(null);

    let deleteModal: any = ref(null);

    const showDeleteModal = () => {
      deleteModal.value.show();
    };

    const hideDeleteModal = () => {
      deleteModal.value.hide();
    };

    const deleting = ref(false);

    const deleteClient = () => {
      const jsConn = new JSConn();
      deleting.value = true;

      jsConn.formParams = {
        id: props.clientId,
      };

      $store.dispatch("deleteClient", jsConn).then(() => {
        ctx.root.$router.push({
          name: NamedRts.clients,
        });
        return Promise.resolve();
      });
    };

    const cancelParams = () => {
      if (props.clientId === "new") {
        return {
          name: NamedRts.clients,
        };
      } else {
        return {
          name: NamedRts.clientShow,
          params: {
            clientId: props.clientId,
          },
        };
      }
    };

    let {
      formClient,
      clientStatuses,
      loadClientStatusesIntoForm,
      loadClientIntoForm,
      loadEmptyPhoneNumber,
      loadPhoneNumberClassificationsIntoForm,
      phoneNumberClassifications,
    } = buildFormClient();

    let { submitButtonText, submitting } = buildSubmitButton();
    let { hasFormErrors, formErrors } = buildFormErrors();

    const apiClient = computed(() => {
      return $store.getters.client as Client;
    });

    onMounted(() => {
      let dataPromises = [];
      let jsConn = new JSConn();

      loadClientStatusesIntoForm(clientStatuses);
      loadPhoneNumberClassificationsIntoForm(phoneNumberClassifications);

      if (props.clientId !== "new") {
        jsConn.formParams = {
          id: props.clientId,
        };

        dataPromises.push($store.dispatch("needClient", jsConn));
      }

      Promise.all(dataPromises)
        .then(() => {
          if (props.clientId !== "new") {
            loadClientIntoForm(apiClient.value, formClient);
          }
        })
        .finally(() => {
          // at this point, check to see if there is a phone
          // number and load an empty one if there are no phone numbers
          loadEmptyPhoneNumber(formClient);
          dataLoaded.value = true;
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
        });
    });

    const onSubmit = (evt: any) => {
      evt.preventDefault();
      const jsConn = new JSConn();

      hasFormErrors.value = false;
      formErrors.value = [];
      submitting.value = true;
      submitButtonText.value = "Submitting...";

      // need to do some special handling on the phone numbers
      // they are a has_many association on the client model
      // if the number is blank, set destroy to true
      let cleanedNumbers = Rmap(ph => {
        if (ph.number) {
          return {
            ...ph,
          };
        } else {
          return {
            ...ph,
            _destroy: true,
          };
        }
      }, formClient.client_phone_numbers_attributes);

      formClient.client_phone_numbers_attributes = cleanedNumbers;

      let storeDispatch;
      if (props.clientId === "new") {
        formClient.id = uuidv4();
        jsConn.formParams = formClient;
        storeDispatch = "createClient";
      } else {
        jsConn.formParams = formClient;
        storeDispatch = "updateClient";
      }

      $store
        .dispatch(storeDispatch, jsConn)
        .then(() => {
          ctx.root.$bvToast.toast(
            "Client was successfully saved",
            TOAST_OPTIONS,
          );
          ctx.root.$router.push(cancelParams());
        })
        .catch(() => {
          hasFormErrors.value = true;
          formErrors.value = jsConn.error["response"]["data"];
        })
        .finally(() => {
          submitting.value = false;
          submitButtonText.value = "Submit";
        });
    };

    return {
      dataLoaded,
      loadingMessage,
      firstInput,
      hasFormErrors,
      formErrors,

      submitButtonText,
      submitting,
      onSubmit,

      cancelParams,

      deleteModal,
      showDeleteModal,
      hideDeleteModal,
      deleteClient,
      deleting,

      apiClient,
      formClient,
      clientStatuses,
      prettifyClientStatus,

      phoneNumberClassifications,
      prettifyPhoneNumberClassification,
    };
  },

  // created() {
  //   if (this.clientId === "new") {
  //     this.client = {
  //       id: uuidv4(),
  //       manager: null,
  //       name: "New",
  //       status: "active",
  //       client_phone_numbers_attributes: [
  //         {
  //           id: null,
  //           number: null,
  //           classification: "cell",
  //           sort_order: 100,
  //           _destroy: false,
  //         },
  //       ],
  //     };
  //     this.dataLoaded = true;
  //     this.$nextTick(() => this.$refs.firstInput.select());
  //   } else {
  //     const jsConn = new JSConn();

  //     jsConn.formParams = {
  //       id: this.clientId,
  //     };

  //     this.$store.dispatch("needClient", jsConn).then(() => {
  //       // correct client is in the store
  //       this.client = {
  //         name: this.apiClient.name,
  //         manager: this.apiClient.manager,
  //         id: this.apiClient.id,
  //         status: this.apiClient.status,
  //         client_phone_numbers_attributes: this.prepareClientPhoneNumbers(),
  //       };
  //       this.dataLoaded = true;
  //       this.cancelParams = {
  //         name: "clientShow",
  //         params: { clientId: this.clientId },
  //       };
  //       this.$nextTick(() => this.$refs.firstInput.select());
  //     });
  //   }
  // },
  // methods: {
  //   // submitForm(evt) {
  //   //   evt.preventDefault();
  //   //   const jsConn = new JSConn();
  //   //   // need to do some special handling on the phone numbers
  //   //   // they are a has_many association on the client model
  //   //   // if the number is blank, set destroy to true
  //   //   let cleanedNumbers = Rmap(ph => {
  //   //     if (ph.number) {
  //   //       return {
  //   //         ...ph,
  //   //       };
  //   //     } else {
  //   //       return {
  //   //         ...ph,
  //   //         _destroy: true,
  //   //       };
  //   //     }
  //   //   }, this.client.client_phone_numbers_attributes);
  //   //   this.client.client_phone_numbers_attributes = cleanedNumbers;

  //   //   jsConn.formParams = this.client;
  //   //   jsConn.toRoutePath = "/clients";

  //   //   let storeDispatch;

  //   //   if (this.clientId === "new") {
  //   //     storeDispatch = "createClient";
  //   //   } else {
  //   //     storeDispatch = "updateClient";
  //   //   }

  //   //   this.$store
  //   //     .dispatch(storeDispatch, jsConn)
  //   //     .then(jsConn => {
  //   //       this.$router.push(jsConn.toRoutePath);
  //   //       return Promise.resolve(jsConn);
  //   //     })
  //   //     .catch(jsConn => {
  //   //       this.hasFormErrors = true;
  //   //       this.formErrors = jsConn.error["response"]["data"];
  //   //     });
  //   // },
  //   // prepareClientPhoneNumbers() {
  //   //   if (this.apiClient.phone_numbers.length > 0) {
  //   //     return Rmap(ph => {
  //   //       return {
  //   //         id: ph.id,
  //   //         number: ph.number,
  //   //         classification: ph.classification,
  //   //         sort_order: ph.sort_order,
  //   //         _destroy: false,
  //   //       };
  //   //     }, this.apiClient.phone_numbers);
  //   //   } else {
  //   //     // if there are no phone numbers, there should still be
  //   //     // the option to add one
  //   //     return [
  //   //       {
  //   //         id: null,
  //   //         number: null,
  //   //         classification: "cell",
  //   //         sort_order: 100,
  //   //         _destroy: false,
  //   //       },
  //   //     ];
  //   //   }
  //   // },

  //   // deleteClient() {
  //   //   const jsConn = new JSConn();
  //   //   jsConn.toRoutePath = "/clients";
  //   //   jsConn.formParams = {
  //   //     id: this.clientId,
  //   //   };
  //   //   this.$store.dispatch("deleteClient", jsConn).then(jsConn => {
  //   //     this.$router.push(jsConn.toRoutePath);
  //   //     return Promise.resolve(jsConn);
  //   //   });
  //   // },
  // },
});
